// Global Instructions Rule Applied!
//
// Deal form — Execution tab (signed-off Mockup 2, rev 3; stage flyout matched
// to the PowerApps design in docs/design/Process step *.png, owner 2026-07-28).
// The timeline IS the control: clicking any stage opens its checklist in a
// compact card that drops down from that stage and overlays the content
// below. Items are editable on any stage; on the active stage the square
// back button / "Next Stage" move the deal and the card follows onto the new
// stage. Stage history opens from the header link. Deals not yet in
// execution get an explanatory state with a manual "Enter execution" action
// (deal_execution__enter, p_via='manual').
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Modal, Spin, Switch, Table, Tag, Tooltip } from 'antd'
import { CheckOutlined, CloseOutlined, LeftOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import RoundCard from './RoundCard.tsx'
import RoundSecuritiesCard from './RoundSecuritiesCard.tsx'
import RoundInvestorsCard from './RoundInvestorsCard.tsx'
import DealTransactionsTable from './DealTransactionsTable.tsx'
import CommittedVsBookedCard from './CommittedVsBookedCard.tsx'
import CapTableSummaryCard from './CapTableSummaryCard.tsx'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import LegalTermsCard from './LegalTermsCard.tsx'
import DealAgeChips from '../../deals/components/pipelines/kanbans/early_stage/DealAgeChips.tsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import DisabledReason from '../../../core/components/DisabledReason.tsx'
import { useCapability } from '../../../core/hooks/useCapability.ts'
import { PipelineConfigModel } from '../../../state_manager/data_models/pipeline_config_model.ts'
import { PipelineStagesModel } from '../../../state_manager/data_models/pipeline_stages_model.ts'
import { daysSince, resolveBandColour } from '../../../core/utils/pipelineAge.ts'
import { DealExecutionChecklistItem } from '../../../state_manager/data_models/deal_execution_board_model.ts'
import { useDealExecutionState } from '../hooks/useDealExecutionState.ts'
import { DealStatesStrip } from '../../internal_agents/components/DealStatesStrip.tsx'
import { AgentSignalsSection } from '../../internal_agents/components/AgentSignalsSection.tsx'

interface ExecutionTabProps {
  dealId: string | null
  /** Execution-follower strip (states + signals) below the timeline. All
   *  optional — the tab renders unchanged when the deal form doesn't wire
   *  them. */
  onNavigateToAgentTab?: () => void
  defaultAssignees?: Array<{ label: string; value: string }>
  onTaskCreated?: () => void
  onRefreshAgentReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Hands the tab's data refetch (execution row + context + legal terms) to
   *  the deal form so its Refresh button drives this tab too — same contract
   *  as DocumentsTab. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
}

interface StageStep {
  pipelineConfigId: string
  stageId: string
  name: string
  sequenceKb: number
}

const ENTRY_VIA_LABELS: Record<string, string> = {
  portfolio: 'Portfolio company — entered automatically on creation',
  committed: 'Committed in origination pipeline',
  manual: 'Manual promotion'
}

export default function ExecutionTab({
  dealId,
  onNavigateToAgentTab,
  defaultAssignees = [],
  onTaskCreated,
  onRefreshAgentReady = null,
  onRefreshReady = null
}: ExecutionTabProps): React.ReactElement {
  const {
    row,
    context,
    loading,
    history,
    historyLoading,
    loadHistory,
    moveToStage,
    toggleChecklistItem,
    enterExecutionManually,
    patchRoundFields,
    linkRound,
    saveInstrument,
    removeInstrument,
    saveParticipation,
    removeParticipation,
    refetch
  } = useDealExecutionState(dealId)
  const { openFormInTab } = useTabManagerContext()
  // docs/USER_RIGHTS_DESIGN.md 6.2: disabled with the reason when the user may not create
  const createRight = useCapability('asset_transactions.create')
  const [viewStageIndex, setViewStageIndex] = useState<number | null>(null)
  // The strip has two loaders (states card row + signals footer); the deal
  // form's Refresh button gets ONE composite through onRefreshAgentReady.
  const statesRefreshRef = useRef<(() => Promise<void>) | null>(null)
  const signalsRefreshRef = useRef<(() => Promise<void>) | null>(null)
  useEffect(() => {
    if (onRefreshAgentReady) {
      onRefreshAgentReady(async () => {
        if (statesRefreshRef.current) await statesRefreshRef.current()
        if (signalsRefreshRef.current) await signalsRefreshRef.current()
      })
    }
  }, [onRefreshAgentReady])

  // The tab's DATA refetch (execution row + context + the Legal Terms card's
  // own payload) — handed up to the deal form's Refresh button and re-driven
  // automatically when the user's attention returns to a stale tab. Without
  // this, an asset transaction booked in another tab never appeared here
  // until a hard refresh (owner report 2026-08-26).
  const legalRefreshRef = useRef<(() => Promise<void>) | null>(null)
  const refreshAll = useCallback(async (): Promise<void> => {
    await refetch()
    if (legalRefreshRef.current) await legalRefreshRef.current()
  }, [refetch])
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(refreshAll)
  }, [onRefreshReady, refreshAll])
  const activateRef = useRefreshOnActivate(refreshAll)
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [moving, setMoving] = useState(false)
  const [entering, setEntering] = useState(false)

  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: PipelineConfigModel[] } }) =>
      state.globalConfigs?.pipeline_configs || []
  )
  const pipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: PipelineStagesModel[] } }) =>
      state.globalConfigs?.pipeline_stages || []
  )

  // The deal's execution pipeline stages, in kanban order.
  const steps: StageStep[] = useMemo(() => {
    if (!row) return []
    return pipelineConfigs
      .filter((cfg) => cfg.pipeline === row.pipelines__id && cfg.visible_kb !== false)
      .map((cfg) => {
        const stage = pipelineStages.find((s) => s.id === cfg.stage)
        return {
          pipelineConfigId: cfg.id as string,
          stageId: cfg.stage as string,
          name: (stage?.name as string) || 'Stage',
          sequenceKb: (cfg.sequence_kb as number | null) ?? 0
        }
      })
      .sort((a, b) => a.sequenceKb - b.sequenceKb)
  }, [row, pipelineConfigs, pipelineStages])

  const currentIndex = useMemo(
    () => steps.findIndex((s) => s.pipelineConfigId === row?.pipeline_config__id),
    [steps, row]
  )
  const daysInStage = row ? daysSince(row.pipeline_deals__stage_date ?? null) : null

  const openHistory = (): void => {
    setHistoryModalOpen(true)
    loadHistory()
  }

  const openStage = (index: number): void => {
    setViewStageIndex(index)
  }

  const handleMove = async (targetIndex: number): Promise<void> => {
    const target = steps[targetIndex]
    if (!target || moving) return
    setMoving(true)
    try {
      await moveToStage(target.pipelineConfigId)
      // Keep the inline stage card open, following onto the stage the deal
      // just moved to (owner request: Back / Next Stage open that card).
      setViewStageIndex(targetIndex)
    } finally {
      setMoving(false)
    }
  }

  const handleEnterExecution = async (): Promise<void> => {
    setEntering(true)
    try {
      await enterExecutionManually()
    } finally {
      setEntering(false)
    }
  }

  if (loading && !row) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  if (!row) {
    return (
      <div className='max-w-xl mx-auto text-center py-16'>
        <h3 className='text-base font-semibold mb-2'>Not in Execution Yet</h3>
        <p className='text-sm text-gray-500 mb-1'>
          A deal enters its execution pipeline automatically when it is committed in origination.
        </p>
        <p className='text-sm text-gray-500 mb-5'>
          Portfolio-company deals enter immediately on creation. You can also promote this deal manually.
        </p>
        <Button type='primary' loading={entering} onClick={handleEnterExecution}>
          Enter execution now
        </Button>
      </div>
    )
  }

  const viewStep = viewStageIndex != null ? steps[viewStageIndex] : null
  const isViewingCurrent = viewStageIndex === currentIndex
  const viewChecklist: DealExecutionChecklistItem[] = viewStep
    ? [...((row.prog__checklists_all || {})[viewStep.pipelineConfigId] || [])].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
      )
    : []

  return (
    <div ref={activateRef} className='px-2 py-4'>
      <div className='flex items-center gap-2 mb-6'>
        <Tag color='blue'>{row.pipelines__name}</Tag>
        {/* The stage's decision status from cfg.pipeline_statuses — a deal in
            the Completed stage reads "Completed", not "In Execution". */}
        <Tag color='green'>{row.pipeline_statuses__name || 'In Execution'}</Tag>
        <span className='text-xs text-gray-400 ml-auto'>
          Click a stage for its checklist and actions · <a onClick={openHistory}>stage history</a>
        </span>
      </div>

      {/* Compact timeline (mockup sign-off 2026-07-28): 24px circles, stage
          name 3px below, days-in-stage as the kanban age chip right of the
          active stage name (same warn/crit bands as the Deal Pipeline cards),
          no description line. Selected stage = variant C: blue semibold name
          + a thin blue stem down to the flyout. */}
      <div className='flex items-start'>
        {steps.map((step, index) => {
          const isDone = index < currentIndex
          const isActive = index === currentIndex
          const isSelected = viewStageIndex === index
          const stageCfg = isActive
            ? pipelineConfigs.find((c) => c.id === row.pipeline_config__id)
            : null
          return (
            <div
              key={step.pipelineConfigId}
              className='relative flex flex-1 cursor-pointer flex-col items-center pb-1'
              onClick={() => openStage(index)}
            >
              {index < steps.length - 1 && (
                <div
                  className={isDone ? 'absolute bg-blue-500' : 'absolute bg-gray-200'}
                  style={{ height: 1.5, top: 11, left: 'calc(50% + 16px)', right: 'calc(-50% + 16px)', zIndex: 1 }}
                />
              )}
              <div
                className={`relative z-[2] flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] text-xs ${
                  isActive
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : isDone
                      ? 'border-blue-500 bg-white text-blue-500'
                      : 'border-gray-300 bg-white text-gray-400'
                }`}
              >
                {isDone ? <CheckOutlined style={{ fontSize: 10 }} /> : index + 1}
              </div>
              <div
                className={`mt-[3px] flex items-center gap-1.5 whitespace-nowrap text-[13px] ${
                  isSelected
                    ? 'font-semibold text-blue-600'
                    : !isDone && !isActive
                      ? 'text-gray-400'
                      : ''
                }`}
              >
                {step.name}
                {isActive && stageCfg?.show_age_counters !== false && daysInStage != null && (
                  <DealAgeChips
                    daysInStage={daysInStage}
                    stageColour={resolveBandColour(
                      daysInStage,
                      stageCfg?.stage_warn_days,
                      stageCfg?.stage_warn_colour,
                      stageCfg?.stage_crit_days,
                      stageCfg?.stage_crit_colour
                    )}
                    daysInPipeline={null}
                    pipelineColour={null}
                  />
                )}
              </div>
              {isSelected && (
                <div
                  className='absolute left-1/2 w-[2px] -translate-x-1/2 rounded bg-blue-500'
                  style={{ top: 48, height: 18, zIndex: 0 }}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Stage flyout (docs/design/Process step *.png + mockup sign-off):
          standard FormSection container with a hover shadow, dropping from
          the clicked stage and OVERLAYING the content below. Title is the
          stage status; only the active stage gets the back / Next Stage
          footer; the card follows the deal after a move. */}
      {viewStep && viewStageIndex != null && (
        <div className='relative z-20 h-0'>
          <FormSection
            title={
              isViewingCurrent
                ? `Active for ${daysInStage ?? 0} day${daysInStage === 1 ? '' : 's'}`
                : viewStageIndex < currentIndex
                  ? 'Completed'
                  : 'Upcoming'
            }
            scrollable={false}
            className='absolute shadow-lg'
            style={{
              width: 440,
              maxWidth: '100%',
              top: 16,
              left: `clamp(0px, calc(${((viewStageIndex + 0.5) * 100) / steps.length}% - 220px), calc(100% - 440px))`,
              transition: 'left 0.2s ease'
            }}
            headerActions={
              <Button
                size='small'
                type='text'
                icon={<CloseOutlined />}
                onClick={() => setViewStageIndex(null)}
              />
            }
          >
            {viewChecklist.length === 0 ? (
              <div className='text-gray-400 text-sm py-2'>No checklist items for this stage.</div>
            ) : (
              <div>
                {viewChecklist.map((item) => (
                  <div key={item.id} className='flex items-center gap-3 py-2'>
                    <span className='flex-1 text-sm'>
                      {item.global_no != null ? `${item.global_no}. ` : ''}
                      {item.label}
                    </span>
                    <span className='text-xs text-gray-400'>
                      {item.answer && item.answered_at
                        ? dayjs(item.answered_at).format('MM/DD/YYYY')
                        : ''}
                    </span>
                    <Switch
                      size='small'
                      checked={item.answer}
                      onChange={(checked) => toggleChecklistItem(item.id, checked)}
                    />
                  </div>
                ))}
              </div>
            )}
            {isViewingCurrent && (
              <div className='flex items-center justify-end gap-2 mt-3'>
                <Button
                  icon={<LeftOutlined />}
                  disabled={currentIndex <= 0 || moving}
                  onClick={() => handleMove(currentIndex - 1)}
                />
                <Button
                  type='primary'
                  loading={moving}
                  disabled={currentIndex >= steps.length - 1}
                  onClick={() => handleMove(currentIndex + 1)}
                >
                  Next Stage
                </Button>
              </div>
            )}
          </FormSection>
        </div>
      )}

      {/* Deal EXECUTION follower (deal split, owner 2026-08-25): the
          execution team's Current State card row, then the execution
          follower's open signals in their own section, just below the stage
          timeline. Execution-team-only by placement — this content never
          appears on the deal Dashboard, and nothing here hands over to the
          company track. The stage flyout above overlays this like it overlays
          the round cards (z-20 h-0). */}
      {dealId ? (
        <div className='mt-6' data-debug='execution-tab-agent-strip'>
          <DealStatesStrip
            dealId={dealId}
            dimension='execution'
            title='Execution State'
            defaultAssignees={defaultAssignees}
            onTaskCreated={onTaskCreated}
            onRefreshReady={(refreshFn) => {
              statesRefreshRef.current = refreshFn
            }}
          />
          <div className='mt-3' data-debug='execution-tab-signals-section'>
            <AgentSignalsSection
              contextTable='deals'
              contextId={dealId}
              openOnly
              maxVisible={3}
              hideWhenEmpty
              collapseKey='deal_execution_signals'
              agentTypeKeys={['deal_execution_follower']}
              onViewMore={onNavigateToAgentTab}
              onRefreshReady={(refreshFn) => {
                signalsRefreshRef.current = refreshFn
              }}
              defaultAssignees={defaultAssignees}
              onTaskCreated={onTaskCreated}
            />
          </div>
        </div>
      ) : null}

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4 mt-8'>
        <RoundCard
          dealId={dealId as string}
          context={context}
          companyId={row.accounts__id}
          companyName={row.accounts__name}
          currencySymbol={row.currencies__symbol}
          onPatchRound={patchRoundFields}
          onLinkRound={linkRound}
        />

        <RoundSecuritiesCard
          context={context}
          companyId={row.accounts__id}
          companyName={row.accounts__name}
          onSaveInstrument={saveInstrument}
          onRemoveInstrument={removeInstrument}
          onSecuritySaved={refetch}
        />
      </div>

      {/* Round Investors (who + how much, early) with the per-fund
          committed-vs-booked rollup condensed to its right (owner layout,
          2026-08-13). */}
      <div className='grid grid-cols-1 xl:grid-cols-3 gap-4 mt-4'>
        <div className='xl:col-span-2'>
          <RoundInvestorsCard
            hasRound={!!context?.round}
            participations={context?.participations || []}
            rollup={context?.commitment_rollup || []}
            usManagerAccountIds={context?.us_manager_account_ids || []}
            currencySymbol={row.currencies__symbol}
            raiseAmount={context?.round?.rounds__raise_amount}
            onSave={saveParticipation}
            onRemove={removeParticipation}
          />
        </div>
        <CommittedVsBookedCard
          rollup={context?.commitment_rollup || []}
          currencySymbol={row.currencies__symbol}
          className='h-full'
        />
      </div>

      <div className='grid grid-cols-1 xl:grid-cols-2 gap-4 mt-4'>
        <CapTableSummaryCard
          capTable={context?.cap_table}
          hasRound={!!context?.round}
          companyId={row.accounts__id}
          companyName={row.accounts__name}
          roundId={context?.round?.rounds__id}
          roundCloseDate={context?.round?.rounds__round_close_date}
          participations={context?.participations || []}
          rollup={context?.commitment_rollup || []}
          usManagerAccountIds={context?.us_manager_account_ids || []}
          currencySymbol={row.currencies__symbol}
          onCaptured={refetch}
        />
        <LegalTermsCard
          roundId={context?.round?.rounds__id}
          companyId={row.accounts__id}
          companyName={row.accounts__name}
          dealId={dealId}
          onRefreshReady={(fn) => {
            legalRefreshRef.current = fn
          }}
        />
      </div>

      <FormSection
        title='Linked Transactions'
        titleColor='green'
        scrollable={false}
        className='mt-4'
        headerActions={
          <>
            <Tooltip title='Refresh'>
              <Button size='small' type='text' icon={<ReloadOutlined />} onClick={() => refetch()} />
            </Tooltip>
            <DisabledReason reason={createRight.reason}>
              <Button
                size='small'
                type='primary'
                icon={<PlusOutlined />}
                disabled={!createRight.allowed}
                onClick={() =>
                  openFormInTab('asset-transactions', null, null, {
                    deal: dealId,
                    company: row.accounts__id
                  })
                }
              >
                Transaction
              </Button>
            </DisabledReason>
          </>
        }
      >
        <DealTransactionsTable
          transactions={context?.transactions || []}
          currencySymbol={row.currencies__symbol}
          emptyText='No asset transactions linked yet — book one with the button above, or link the deal on an existing transaction in the booking screen.'
        />
      </FormSection>

      {/* Stage-history modal */}
      <Modal
        title='Stage History'
        open={historyModalOpen}
        onCancel={() => setHistoryModalOpen(false)}
        footer={null}
        width={640}
      >
        <Table
          size='small'
          rowKey={(r, i) => `${r.entry_type}-${i}`}
          loading={historyLoading}
          pagination={false}
          dataSource={history}
          columns={[
            {
              title: 'Event',
              key: 'event',
              render: (_, r) =>
                r.entry_type === 'entered'
                  ? `Entered execution${r.via ? ` — ${ENTRY_VIA_LABELS[r.via] || r.via}` : ''}`
                  : 'Stage change'
            },
            { title: 'From', dataIndex: 'from_stage', render: (v) => v || '—' },
            { title: 'To', dataIndex: 'to_stage', render: (v) => v || '—' },
            {
              title: 'When',
              dataIndex: 'occurred_at',
              render: (v) => (v ? dayjs(v).format('MM/DD/YYYY HH:mm') : '—')
            }
          ]}
        />
      </Modal>
    </div>
  )
}
